import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Grid,
  IconButton,
  ListItem,
  Menu,
  MenuItem,
  TextField,
} from '@mui/material';
import GlobalFilterModal from 'components/modals/GlobalFilterModal';
import { PublicmapContext } from 'context/PublicmapContext';
import { ThemeContext } from 'context/themes';
import { UserContext } from 'context/UserContext';
import { useSession } from 'next-auth/react';
import getConfig from 'next/config';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { MouseEvent, useContext, useEffect, useState } from 'react';
import { useGlobalFilterStore } from 'stores/filters/';
import { clearGlobalFilterFromSessionStorage } from 'stores/filters/globalFilterStore';
import { formatToKebabCase } from '../../utils/helpers';

const { publicRuntimeConfig } = getConfig();

export const ContextSwitchMenu = ({ userContext }) => {
  const { data: session } = useSession();
  const userInfo = session?.userInfo;
  const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);
  const [selectedTenant, setSelectedTenant] = useState<string>('');
  const [listOfTenants, setListOfTenants] = useState<any[]>([]);
  const [openGlobalFilters, setOpenGlobalFilters] = useState(false);

  const globalFilterState = useGlobalFilterStore.getState();

  const router = useRouter();
  const currentPage = router.pathname.toLowerCase();
  const pagesWithGlobalFilters = ['inventory', 'replacement', 'fieldreplacement'];
  const hideGlobalFilterForSubRoutes = ['prereplacementinspection', 'postreplacementinspection'];
  const hideGlobalFilterIcon = hideGlobalFilterForSubRoutes.some((subPage) =>
    currentPage.includes(subPage)
  );
  const showGlobalFilterIcon =
    !hideGlobalFilterIcon && pagesWithGlobalFilters.some((page) => currentPage.includes(page));

  const activeFiltersCount = () =>
    Object.keys(globalFilterState).reduce((acc: any, resource: any) => {
      // For each entity in the global store, count the filters that are not empty.
      if ('filter' in globalFilterState[resource]) {
        return (
          acc +
          Object.values(globalFilterState[resource].filter).reduce(
            (acc: any, val: any) => acc + (val.length && 1),
            0
          )
        );
      }
    }, 0);

  const parsedToken = userInfo?.tokenParsed;

  useEffect(() => {
    if (parsedToken?.resource_access) {
      const tenants = parsedToken?.resource_access;
      if (tenants) {
        const tenantNames = Object.keys(tenants);
        const accessibleTenantNames = tenantNames.filter((tn) =>
          tenants[tn].roles.map((r) => (r.includes('leadcast') ? 1 : 0)).includes(1)
        );
        setListOfTenants(accessibleTenantNames);
      }
    }
  }, []);

  const open = Boolean(anchorEl);

  const buttonStyle = {
    height: '6vh',
    width: '3vw',
    borderRadius: 0,
    backgroundColor: open ? '#014954' : 'transparent',
    color: open ? `#fff` : '#000',
  };

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTenantChange = (event) => {
    //Set selection locally
    setSelectedTenant(event.target.value);
    //Set Session Storage tenantName to drive the updated graphQL query
    userContext.persistTenantName(event.target.value);
    clearGlobalFilterFromSessionStorage();
    //Refresh the page to trigger the updated data after a brief delay so the refresh isn't jarring for the user
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  //Some tenants are lowercase on the first name so this is just here to keep names consistent for the user
  const captilize = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  //return the initials of the user for their avatar. Pictures may be added in the future but for now it will simply be the initials.
  const stringAvatar = () => {
    if (!userInfo) return null;
    return userInfo.firstName.charAt(0).toUpperCase() + userInfo.lastName.charAt(0).toUpperCase();
  };

  const Tenants = () => {
    return (
      listOfTenants && (
        <TextField
          data-test-id="top-navbar-account-menu-switch-tenants"
          label="Switch Client/Tenant"
          value={selectedTenant}
          select
          sx={{
            width: '230px',
            marginInlineStart: '1%',
            marginTop: '8px',
            '& .MuiInputBase-root': { height: '35px' },
            '& .MuiInputLabel-root': {
              fontSize: '14px',
              fontWeight: 'bold',
              position: 'relative',
              top: '25%',
            },
          }}
          SelectProps={{
            MenuProps: {
              sx: {
                zIndex: 9003, // TODO: Revisit, z-indices are getting too high/out of hand
              },
            },
          }}
          onChange={(event) => handleTenantChange(event)}
        >
          {listOfTenants.map((tenant: any, index: number) => {
            return (
              <MenuItem
                id={`${tenant}`}
                data-test-id={`top-navbar-account-menu-tenant-switch-${formatToKebabCase(tenant)}`}
                key={`${index}-${tenant}`}
                value={tenant}
                style={{ fontSize: '16px' }}
              >
                {captilize(tenant)}
              </MenuItem>
            );
          })}
        </TextField>
      )
    );
  };

  return (
    <div style={{ marginInlineStart: 'auto', paddingInlineEnd: '15px' }}>
      <div>
        {showGlobalFilterIcon && (
          <IconButton
            id="-globalfilter-button"
            data-test-id="top-nav-global-filter-button"
            style={buttonStyle}
            sx={{
              height: '40px',
              width: '40px',
              minWidth: '50px',
              '&:hover': { backgroundColor: 'gray' },
            }}
            onClick={() => {
              setOpenGlobalFilters(true);
            }}
          >
            <Badge badgeContent={activeFiltersCount()} color="warning">
              <FilterListIcon style={{ fontSize: '28px' }} />
            </Badge>
          </IconButton>
        )}
        <IconButton
          id="account-button"
          data-test-id="top-nav-account-button"
          style={buttonStyle}
          sx={{
            height: '40px',
            width: '40px',
            minWidth: '50px',
            '&:hover': { backgroundColor: 'gray' },
          }}
          onClick={handleClick}
        >
          <AccountCircleOutlinedIcon style={{ fontSize: '28px' }} />
        </IconButton>
      </div>
      <Menu
        sx={{ zIndex: 9002 }}
        disableScrollLock={true}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <ListItem
          data-test-id="top-navbar-account-menu"
          style={{
            backgroundColor: 'transparent',
            height: '270px',
            width: '260px',
            outline: 'none',
          }}
        >
          <Grid container>
            <Grid
              item
              xs={12}
              style={{
                justifyContent: 'center',
                display: 'flex',
                borderRadius: '50%',
                paddingBottom: '4%',
              }}
            >
              <Avatar
                data-test-id="top-navbar-account-menu-user-initials"
                sx={{ bgcolor: '#014954', width: 66, height: 66 }}
              >
                {stringAvatar()}
              </Avatar>
            </Grid>
            <Grid item xs={12} style={{ textAlign: 'center', marginBottom: '1%' }}>
              <div className="data-text">
                <p style={{ fontSize: '15px' }} data-test-id="top-navbar-account-menu-user-name">
                  {userInfo?.fullName}
                </p>
              </div>
            </Grid>
            <Grid item xs={12} style={{ textAlign: 'center', marginBottom: '2%' }}>
              <div className="data-text">
                <p
                  data-test-id="top-navbar-account-menu-tenant-name"
                  style={{ fontSize: '15px', fontWeight: 'normal' }}
                >
                  {userContext.tenant?.data?.displayName}
                </p>
              </div>
            </Grid>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                width: '100%',
                marginTop: '10px',
              }}
            >
              <Button
                data-test-id="top-navbar-account-menu-view-account-button"
                variant="outlined"
                size={'small'}
                style={{ marginInlineEnd: '4%', color: '#014954' }}
              >
                <Link href={'./Settings'}>
                  <a style={{ color: '#014954' }}>View Account</a>
                </Link>
              </Button>
              <Button
                data-test-id="top-navbar-account-menu-sign-out-button"
                variant="outlined"
                size={'small'}
                style={{ color: '#014954' }}
              >
                <Link href={'/auth/signout'}>
                  <a style={{ color: '#014954' }}>Sign Out</a>
                </Link>
              </Button>
            </div>
            <Tenants />
          </Grid>
        </ListItem>
      </Menu>
      <GlobalFilterModal open={openGlobalFilters} setOpen={setOpenGlobalFilters} />
    </div>
  );
};

const TopBar = (props) => {
  const userContext = useContext(UserContext);
  const themeContext = useContext(ThemeContext);
  const publicMapContext = useContext(PublicmapContext);

  const router = useRouter();

  const tenantDisplayName =
    publicMapContext?.tenantDisplayName && publicMapContext.tenantDisplayName.trim() !== ''
      ? publicMapContext.tenantDisplayName
      : userContext?.tenant?.data?.displayName && userContext.tenant.data.displayName.trim() !== ''
      ? userContext.tenant.data.displayName
      : router?.query?.tenantName;

  const { currentThemeColor } = themeContext;

  return (
    <Box
      className="topbar"
      sx={{
        backgroundColor: currentThemeColor.background.topBar,
        overflow: 'hidden',
        boxShadow: 4,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '.5vmax',
          alignItems: 'center',
          flexWrap: 'nowrap',
        }}
      >
        <div className="navbar-logo" data-test-id="top-navbar-logo">
          <img
            src={`${publicRuntimeConfig.basePath || ''}/assets/leadCAST_logo_only_inverse.png`}
            className="logo"
          />
        </div>
        <div className="navbar-text" data-test-id="top-navbar-tenant-name">
          {tenantDisplayName}
        </div>
      </div>
      {!props.hideTenantSwitcher && <ContextSwitchMenu userContext={userContext} />}
    </Box>
  );
};

export default TopBar;
